import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { BaseAuthenticationBloc } from '@kauth';
import { SubscriptionBloc } from '@kbloc';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import { PromoBloc } from '@kp/shared/components/promo/promo.bloc';
import { PremiumGateService } from '@kp/shared/services/premium-gate.service';
import { RevenueCatWebBillingService, WINDOW } from '@kservice';
import { CardRequestType } from '@ktypes/enums';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-screen-premium-paywall',
  templateUrl: './screen-premium-paywall.component.html',
  styleUrls: ['./screen-premium-paywall.component.scss'],
})
export class ScreenPremiumPaywallComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroy$ = new Subject<void>();
  private _pollTimer: any;
  private _autoStarted = false;
  private _pendingAutoStart = false;
  private _viewReady = false;
  private _paywallObserver: MutationObserver | null = null;
  private _paywallRendered = false;
  private _paywallRenderFallbackTimer: number | null = null;
  private _paywallClosedHandled = false;
  private _paywallClosePoller: number | null = null;
  private _paywallMessageListener: ((event: MessageEvent) => void) | null = null;
  private _purchaseHandled = false;

  private _paywallContainer: ElementRef<HTMLDivElement> | null = null;
  @ViewChild('paywallContainer')
  set paywallContainerRef(ref: ElementRef<HTMLDivElement> | null) {
    if (ref?.nativeElement) {
      this._paywallContainer = ref;
      this._viewReady = true;
      this._attemptAutoStart();
    }
  }

  offeringId: string | null = null;
  returnUrl: string | null = null;
  dismissible = true;
  error: string | null = null;
  paywallStarted = false;
  paywallRendered = false;
  upgradeUnavailable = false;
  overflowMenuOpen = false;

  constructor(
    private _authBloc: BaseAuthenticationBloc,
    private _revenueCat: RevenueCatWebBillingService,
    private _subscriptionBloc: SubscriptionBloc,
    private _cardBloc: CardBloc,
    private _promoBloc: PromoBloc,
    private _premiumGate: PremiumGateService,
    private _analyticsBloc: AnalyticsBloc,
    private _router: Router,
    @Inject(WINDOW) private _window: Window
  ) {}

  private _sendPaywallEvent(event: AnalyticEvent, extra?: Record<string, unknown>) {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.paywall,
        event,
        meta: {
          source: 'web',
          type: this.dismissible ? 'soft' : 'hard',
          offeringId: this.offeringId,
          ...extra,
        },
      })
    );
  }

  ngOnInit() {
    // Discard any cached promo list (and invalidate any in-flight pre-paywall
    // getPromos response) so it cannot trigger the featured-promo modal under
    // the paywall overlay or persist past purchase. Promos are re-fetched in
    // ngOnDestroy when the paywall closes (purchase or dismiss).
    this._promoBloc.reset();

    this._authBloc.premiumPaywallContext$.pipe(takeUntil(this._destroy$)).subscribe((ctx) => {
      this.offeringId = ctx?.offeringId || null;
      this.returnUrl = ctx?.returnUrl || '/today';
      this.dismissible = ctx?.dismissible ?? true;
      this.upgradeUnavailable = false;
      this.error = null;

      if (this.offeringId && !this._autoStarted && !this.paywallStarted) {
        this._pendingAutoStart = true;
        this._attemptAutoStart();
      }
    });

    // If user became premium (e.g. after returning from checkout), poll and redirect back.
    // RevenueCat entitlement sync can take a few seconds after checkout.
    this._startStatusPolling();
    this._subscriptionBloc.subscriptionStatus$.pipe(takeUntil(this._destroy$)).subscribe((status) => {
      // Side menu route (`/get-premium`) does not always set premiumPaywallContext.
      // Derive offering id from canonical subscription status as a fallback.
      if (!this.offeringId) {
        const fallbackOfferingId = status?.data?.canonicalCanPurchase ? status?.data?.canonicalOfferingId : null;
        if (fallbackOfferingId) {
          this.offeringId = fallbackOfferingId;
          if (!this._autoStarted && !this.paywallStarted) {
            this._pendingAutoStart = true;
            this._attemptAutoStart();
          }
        }
      }

      if (status?.data?.canonicalHasAccess && !this._purchaseHandled) {
        // Subscription polling + BehaviorSubject re-emissions can deliver
        // canonicalHasAccess === true multiple times before this component is
        // destroyed. Guard so the post-purchase side effects (analytics,
        // refreshes, navigation) only fire once and stop polling immediately.
        this._purchaseHandled = true;
        clearTimeout(this._pollTimer);
        this._premiumGate.markPremiumUnlockedNow();
        this._sendPaywallEvent(AnalyticEvent.paywall_purchase_completed);
        this._cardBloc.refresh(
          [CardRequestType.TRACKING, CardRequestType.AVAILABLE, CardRequestType.RECOMMENDED],
          null,
          null,
          true
        );
        // Promos (e.g. freemium-eligible recommendations) are gated on the user's
        // pre-purchase access state, so refresh them to drop now-stale entries.
        this._promoBloc.getPromos();
        this._authBloc.resetPremiumPaywallContext();
        const target = this.returnUrl || '/today';
        if (this._router.url !== target) {
          void this._router.navigateByUrl(target);
        }
      }
    });
  }

  ngOnDestroy() {
    clearTimeout(this._pollTimer);
    this._stopPaywallObserver();
    this._stopPaywallClosePoller();
    this._stopPaywallMessageListener();
    // Re-populate promos after the paywall closes (purchase or dismiss) since
    // we cleared them in ngOnInit. Cancel-previous semantics in PromoBloc
    // ensure this is the response that ultimately wins.
    this._promoBloc.getPromos();
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngAfterViewInit() {
    this._viewReady = true;
    this._attemptAutoStart();
  }

  private _attemptAutoStart() {
    if (!this._viewReady || !this._pendingAutoStart || this.paywallStarted) return;
    // Defer to the next tick to avoid ExpressionChangedAfterItHasBeenChecked.
    setTimeout(() => {
      if (this.paywallStarted || !this._pendingAutoStart) return;
      this._pendingAutoStart = false;
      this._autoStarted = true;
      void this.continueToCheckout();
    }, 0);
  }

  private _startStatusPolling() {
    let attempts = 0;
    const poll = () => {
      attempts += 1;
      this._subscriptionBloc.getSubscriptionStatus(0, { forceRefresh: true });
      if (attempts >= 10) {
        return;
      }
      this._pollTimer = setTimeout(poll, 2000);
    };
    poll();
  }

  async continueToCheckout() {
    if (!this.offeringId) {
      this.error = 'Missing offering id';
      return;
    }
    if (!this._paywallContainer?.nativeElement) {
      this.error = 'Paywall container is not ready';
      return;
    }
    this.error = null;

    this.paywallStarted = true;
    this._paywallRendered = false;
    this.paywallRendered = false;
    this._paywallClosedHandled = false;
    this._sendPaywallEvent(AnalyticEvent.paywall_presented);
    this._startPaywallObserver();
    this._startPaywallClosePoller();
    this._startPaywallMessageListener();
    this._startPaywallRenderFallbackTimer();
    try {
      await this._revenueCat.presentPaywall({
        htmlTarget: this._paywallContainer.nativeElement,
        offeringId: this.offeringId,
      });
      this._handlePaywallClosed();
    } catch (e: any) {
      // Also used for strict offering validation failures in web billing service.
      console.warn('Unable to open selected paywall offering', e);
      this.error = null;
      this.upgradeUnavailable = true;
      this.paywallStarted = false;
      this.paywallRendered = false;
      this._sendPaywallEvent(AnalyticEvent.paywall_upgrade_unavailable);
      this._stopPaywallRenderFallbackTimer();
      this._stopPaywallObserver();
      this._stopPaywallClosePoller();
      this._stopPaywallMessageListener();
    }
  }

  acknowledgeUpgradeUnavailable() {
    this.notNow();
  }

  onUpgradeUnavailableOpenChange(isOpen: boolean) {
    if (!isOpen) {
      this.acknowledgeUpgradeUnavailable();
    }
  }

  private _startPaywallObserver() {
    if (this._paywallObserver || !this._paywallContainer?.nativeElement) {
      return;
    }
    const Observer = (this._window as Window & { MutationObserver?: typeof MutationObserver }).MutationObserver;
    if (!Observer) return;
    const container = this._paywallContainer.nativeElement;
    this._paywallObserver = new Observer(() => {
      if (this._hasPaywallContent()) {
        this._markPaywallRendered();
        return;
      }
      if (this._paywallRendered && !this._paywallClosedHandled) {
        this._handlePaywallClosed();
      }
    });
    this._paywallObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
  }

  private _stopPaywallObserver() {
    this._paywallObserver?.disconnect();
    this._paywallObserver = null;
  }

  private _startPaywallClosePoller() {
    if (this._paywallClosePoller !== null) return;
    this._paywallClosePoller = this._window.setInterval(() => {
      if (this._paywallClosedHandled) return;
      const frame = this._findPaywallFrame();
      if (this._hasPaywallContent()) {
        if (!this._paywallRendered) {
          this._markPaywallRendered();
        }
        if (frame && !this._isElementVisible(frame)) {
          this._handlePaywallClosed();
        }
        return;
      }
      if (this._paywallRendered) {
        this._handlePaywallClosed();
      }
    }, 500);
  }

  private _stopPaywallClosePoller() {
    if (this._paywallClosePoller === null) return;
    this._window.clearInterval(this._paywallClosePoller);
    this._paywallClosePoller = null;
  }

  private _startPaywallRenderFallbackTimer() {
    this._stopPaywallRenderFallbackTimer();
    this._paywallRenderFallbackTimer = this._window.setTimeout(() => {
      if (!this._paywallClosedHandled && this.paywallStarted && !this._paywallRendered) {
        this._markPaywallRendered();
      }
    }, 1200);
  }

  private _stopPaywallRenderFallbackTimer() {
    if (this._paywallRenderFallbackTimer === null) return;
    this._window.clearTimeout(this._paywallRenderFallbackTimer);
    this._paywallRenderFallbackTimer = null;
  }

  private _startPaywallMessageListener() {
    if (this._paywallMessageListener) return;
    this._paywallMessageListener = (event: MessageEvent) => {
      if (!this.paywallStarted || this._paywallClosedHandled) return;
      if (!this._isPaywallEventSource(event)) return;
      const origin = event.origin || '';
      // If the message is coming from the paywall iframe, trust the source even if
      // the origin is blank or unexpected (some SDKs post from sandboxed frames).
      if (origin && origin !== 'null' && !this._isRevenueCatOrigin(origin)) return;
      if (this._isPaywallCloseMessage(event.data)) {
        this._handlePaywallClosed();
      }
    };
    this._window.addEventListener('message', this._paywallMessageListener);
  }

  private _stopPaywallMessageListener() {
    if (!this._paywallMessageListener) return;
    this._window.removeEventListener('message', this._paywallMessageListener);
    this._paywallMessageListener = null;
  }

  private _isRevenueCatOrigin(origin: string): boolean {
    if (!origin) return false;
    if (origin === 'null') {
      return true;
    }
    return origin.endsWith('revenue.cat') || origin.endsWith('revenuecat.com') || origin.endsWith('paywalls.com');
  }

  private _isPaywallEventSource(event: MessageEvent): boolean {
    const frame = this._findPaywallFrame();
    if (!frame?.contentWindow || !event.source) return false;
    return event.source === frame.contentWindow;
  }

  private _isPaywallCloseMessage(data: unknown): boolean {
    if (!data) return false;
    if (typeof data === 'string') {
      return /close|dismiss|cancel/i.test(data);
    }
    if (typeof data === 'object') {
      const payload = data as Record<string, unknown>;
      const candidates = [payload.type, payload.event, payload.name, payload.action, payload.message, payload.status];
      return candidates.some((value) => typeof value === 'string' && /close|dismiss|cancel/i.test(value));
    }
    return false;
  }

  private _findPaywallFrame(): HTMLIFrameElement | null {
    if (!this._paywallContainer?.nativeElement) return null;
    return this._paywallContainer.nativeElement.querySelector('iframe');
  }

  private _hasPaywallContent(): boolean {
    const container = this._paywallContainer?.nativeElement;
    if (!container) return false;
    if (this._findPaywallFrame()) return true;
    return container.childElementCount > 0;
  }

  private _isElementVisible(element: HTMLElement): boolean {
    const style = this._window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  private _markPaywallRendered() {
    this._paywallRendered = true;
    this.paywallRendered = true;
    this._stopPaywallRenderFallbackTimer();
  }

  private _handlePaywallClosed() {
    if (this._paywallClosedHandled) return;
    this._paywallClosedHandled = true;
    this.paywallRendered = false;
    this._stopPaywallRenderFallbackTimer();
    this._stopPaywallObserver();
    this._stopPaywallClosePoller();
    this._stopPaywallMessageListener();
    this._subscriptionBloc.getSubscriptionStatus(0, { forceRefresh: true });
    this._subscriptionBloc.subscriptionStatus$
      .pipe(
        filter((status) => !!status && status.status !== undefined),
        take(1),
        takeUntil(this._destroy$)
      )
      .subscribe((status) => {
        if (status?.data?.canonicalHasAccess) {
          return;
        }
        if (!this.dismissible) {
          this.paywallStarted = false;
          this._autoStarted = false;
          this._pendingAutoStart = true;
          this._attemptAutoStart();
          return;
        }
        this._authBloc.resetPremiumPaywallContext();
        this.notNow();
      });
  }

  notNow() {
    if (!this.dismissible) return;
    this._sendPaywallEvent(AnalyticEvent.paywall_dismissed);
    const currentUrl = this._router.url || '';
    const openedOnDedicatedPaywallRoute =
      currentUrl === '/get-premium' || currentUrl.startsWith('/get-premium?') || currentUrl.startsWith('/get-premium#');
    const targetUrl = this.returnUrl || '/today';

    this.paywallRendered = false;
    this.paywallStarted = false;
    this.upgradeUnavailable = false;
    this.error = null;
    this._stopPaywallRenderFallbackTimer();
    this._stopPaywallObserver();
    this._stopPaywallClosePoller();
    this._stopPaywallMessageListener();
    this._authBloc.resetPremiumPaywallContext();
    if (openedOnDedicatedPaywallRoute && currentUrl !== targetUrl) {
      void this._router.navigateByUrl(targetUrl);
    }
  }

  signOut() {
    this._sendPaywallEvent(AnalyticEvent.paywall_sign_out);
    this._authBloc.resetPremiumPaywallContext();
    this._authBloc.logout();
  }

  getHelp() {
    this._sendPaywallEvent(AnalyticEvent.paywall_get_help);
    this._window.open('https://support.kumanu.com', '_blank');
  }

  toggleOverflowMenu() {
    this.overflowMenuOpen = !this.overflowMenuOpen;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.overflowMenuOpen) {
        this.overflowMenuOpen = false;
        event.stopPropagation();
        return;
      }
      if (this.dismissible) {
        this.notNow();
      }
    }
  }
}
